import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { roundToClosest } from "utils/math/round-to-closest";
import { CommandAction } from "utils/helpers/classes/command-controller";
import { Texture } from "../../../texture";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { SelectionComponent } from "./selection-component";

import rotationSVG from "assets/webgl2/rotate.svg";
import rotationDownSVG from "assets/webgl2/rotate-down.svg";
import rotationCursorSVG from "assets/webgl2/rotate-cursor.svg";

export class SelectionRotationComponent extends Webgl2Component {
  private readonly _rotationImageTexture: Texture;

  private _size = 32;

  private _prevUnitRotation = 0;
  private _finalDegrees = 0;
  private _componentOriginCenterPosition = Vec2.zero();
  private _prevOrigin = Vec2.zero();
  private _mouseDownPosition = Vec2.zero();
  private _prevTransform: Webgl2Component["transform"];
  private _prevTexture: Texture;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _selectionComponent: SelectionComponent,
  ) {
    super();

    this._rotationImageTexture = new Texture();
    this._rotationImageTexture.loadSource(rotationSVG).then(this._renderer.render);

    this.transform.setSize(this._size, this._size);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return false;

    this._rotationImageTexture.loadSource(rotationDownSVG).then(this._renderer.render);
    this._renderer.canvas.style.cursor = `url("${rotationCursorSVG}"), auto`;

    this._mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e).negateY();
    this._prevTransform = this._selectedComponent.transform.clone();
    this._prevTexture = this._selectedComponent.state.texture.clone();

    this._componentOriginCenterPosition = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.half(),
    );
    this._prevOrigin = this._componentOriginCenterPosition.clone().negateY();
    const unitVec = this._mouseDownPosition.clone().sub(this._prevOrigin);
    this._prevUnitRotation = unitVec.unitDegrees();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const currentMousePos = this._renderer.camera.getGlobalMousePosition(e).negateY();
    const unitVec = currentMousePos.sub(this._prevOrigin);
    const diffDegrees = (unitVec.unitDegrees() - this._prevUnitRotation) % 360;

    const degreesToSet = roundToClosest(this._prevTransform.degrees + diffDegrees, 0.5);
    this._finalDegrees = degreesToSet;

    this._selectionComponent.transform.calcDimensions({
      position: this._componentOriginCenterPosition,
      rotation: degreesToSet,
      roundPosition: false,
    });
    this._selectedComponent.transform.calcDimensions({
      position: this._componentOriginCenterPosition,
      rotation: degreesToSet,
      roundPosition: false,
    });
  }

  public onMouseUp(e: MouseEvent | TouchEvent) {
    this._renderer.canvas.style.cursor = "default";
    this._rotationImageTexture.loadSource(rotationSVG).then(this._renderer.render);
    const component = this._selectedComponent;
    if (!component) return;

    const currentMousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const mouseDiff = currentMousePos.sub(this._mouseDownPosition);
    if (mouseDiff.isZero() || !this._finalDegrees) return;

    const actionTransform = component.transform.clone();
    const actionTexture = component.state.texture;
    const actionFinalDegrees = this._finalDegrees;

    const undoTransform = this._prevTransform;
    const undoTexture = this._prevTexture;
    const undoFinalDegrees = -this._finalDegrees;

    this._renderer.commands.execute(
      new CommandAction(
        "rotate",
        () => {
          this._renderer.selectComponent(component);
          this._selectionComponent.transform.copy(actionTransform);
          component.state.texture = actionTexture.clone();
          component.transform.copy(actionTransform);
          component.emitter.emit("rotationOffset", actionFinalDegrees);
        },
        () => {
          this._renderer.selectComponent(component);
          this._selectionComponent.transform.copy(undoTransform);
          component.state.texture = undoTexture.clone();
          component.transform.copy(undoTransform);
          component.emitter.emit("rotationOffset", undoFinalDegrees);
        },
      ),
    );
  }

  public getRenderData() {
    if (!this._rotationImageTexture.hasData) return;

    const material = Webgl2.materials.default;
    material.setTexture("u_texture0", this._rotationImageTexture);
    return { material };
  }
}

export default SelectionRotationComponent;
